
/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useMemo } from "react";
import { AnimatePresence } from "framer-motion";
import { getDashboardAnalytics, DashboardAnalytics } from '../../services/analytics';
import { getWalletTransactions } from '../../services/transactionService';
import { Transaction, ViewState } from '../../types';
import { useWallet } from '../../context/WalletContext';
import { useAuth } from '../../hooks/useAuth';
import { GenerateReportModal } from './dashboard/GenerateReportModal';
import { SendModal } from './wallet/SendModal';
import { DepositModal } from './wallet/DepositModal';

// Modular Dashboard Components
import { ActionStrip } from './dashboard/ActionStrip';
import { AnalyticsCard } from './dashboard/AnalyticsCard';
import { RecentTransactions } from './dashboard/RecentTransactions';
import { SwapWidget } from './dashboard/SwapWidget';
import { GhostCardPreview } from './dashboard/GhostCardPreview';
import { ZKRelayNode } from './dashboard/ZKRelayNode';
import { QuickContract } from './dashboard/QuickContract';

export const DashboardView: React.FC<{ onNavigate: (view: ViewState) => void }> = ({ onNavigate }) => {
  const { user } = useAuth();
  const { balances, walletAddress, isConnected } = useWallet();
  const [analytics, setAnalytics] = useState<DashboardAnalytics | null>(null);
  const [recentTransactions, setRecentTransactions] = useState<Transaction[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showReportModal, setShowReportModal] = useState(false);
  
  const [showSend, setShowSend] = useState(false);
  const [showReceive, setShowReceive] = useState(false);

  const tokenMap = useMemo(() => new Map(balances.map(token => [token.type, token])), [balances]);
  const portfolioValue = useMemo(() => balances.reduce((acc, t) => acc + (t.balance * t.priceUsd), 0), [balances]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const analyticsData = await getDashboardAnalytics();
        setAnalytics(analyticsData);
        if (walletAddress) {
          const { transactions } = await getWalletTransactions(walletAddress, 1, 15);
          setRecentTransactions(transactions.map(tx => ({ 
            ...tx, 
            usdAmount: tokenMap.get(tx.asset) ? tx.amount * (tokenMap.get(tx.asset)?.priceUsd || 0) : undefined 
          })));
        }
      } finally { setIsLoading(false); }
    };
    fetchData();
  }, [walletAddress, tokenMap]);

  const dashboardTitle = user?.role === 'merchant' ? 'Business Dashboard' : 'Personal Dashboard';

  return (
    <div className="space-y-6 animate-fade-in max-w-[1400px] mx-auto pb-24 px-4 sm:px-0">
      
      {/* 1. High Impact Hero Action Strip */}
      <ActionStrip 
        portfolioValue={portfolioValue}
        title={dashboardTitle}
        onNavigate={onNavigate}
        onSend={() => isConnected ? setShowSend(true) : onNavigate('wallet')}
        onReceive={() => isConnected ? setShowReceive(true) : onNavigate('wallet')}
        onAudit={() => setShowReportModal(true)}
      />

      {/* 2. New Contract Form */}
      <div className="space-y-4">
          <div className="flex items-center gap-3 px-2">
             <div className="w-1 h-4 bg-nobel-gold rounded-full"></div>
             <span className="text-[10px] font-black text-stone-500 uppercase tracking-[0.3em]">Quick Actions</span>
          </div>
          <QuickContract />
      </div>

      {/* 3. Logical Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          
          {/* Section: Analytics */}
          <div className="lg:col-span-8 space-y-6 flex flex-col">
              <div className="flex items-center gap-3 px-2">
                 <div className="w-1 h-4 bg-nobel-gold rounded-full"></div>
                 <span className="text-[10px] font-black text-stone-500 uppercase tracking-[0.3em]">Analytics</span>
              </div>
              
              <AnalyticsCard 
                data={analytics?.revenueChartData || []}
                isLoading={isLoading}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <GhostCardPreview />
                    <ZKRelayNode />
              </div>
          </div>

          {/* Section: Activity */}
          <div className="lg:col-span-4 space-y-6 flex flex-col">
              <div className="flex items-center gap-3 px-2">
                 <div className="w-1 h-4 bg-nobel-gold rounded-full"></div>
                 <span className="text-[10px] font-black text-stone-500 uppercase tracking-[0.3em]">Recent Activity</span>
              </div>

              <SwapWidget tokenMap={tokenMap} />

              <RecentTransactions 
                transactions={recentTransactions}
                isLoading={isLoading}
                onNavigate={onNavigate}
                tokenMap={tokenMap}
              />
          </div>
      </div>

      {/* Modals */}
      <AnimatePresence>
        {showReportModal && <GenerateReportModal onClose={() => setShowReportModal(false)} onSuccess={() => {}} />}
        {showSend && <SendModal onCancel={() => setShowSend(false)} onComplete={() => setShowSend(false)} balances={balances} />}
        {showReceive && <DepositModal onClose={() => setShowReceive(false)} address={walletAddress} balances={balances} />}
      </AnimatePresence>
    </div>
  );
};
